// Monitoring & payment-rail API — reads for the Dashboard/Transactions/Accounts/Gateways screens plus the
// manage:config-gated configure/diagnostic actions. Types mirror the Go view structs (money fields are strings).
import { api, type Paged } from './client'

export type Summary = {
  inflow_count: number
  inflow_total: string
  outflow_count: number
  outflow_total: string
  delivered: number
  failed: number
  reversed: number
  pending: number
  stuck: number
  accounts: number
  stk_pending: number
  events_stuck: number
}

export type Account = {
  id: string
  name: string
  number: string
  currency: string
  role: string
  dispatch_active: boolean
  active: boolean
  bank_name: string
  swift_code: string
  provider?: string
  has_credentials: boolean
}

export type TransactionIn = {
  id: string
  account_name: string
  account_number: string
  amount: string
  reference: string
  transaction_id: string
  source_number: string
  payer_name: string
  narration: string
  created_at?: string
}

export type TransactionOut = {
  id: string
  account_name: string
  account_number: string
  amount: string
  beneficiary: string
  reference: string
  instruction_id: string
  status: string
  acceptance_ref?: string
  response_ref?: string
  fail_reason?: string
  stuck?: boolean
  reconciling?: boolean
  created_at?: string
}

export type StkRequest = {
  id: string
  account_name: string
  account_number: string
  phone: string
  amount: string
  reference: string
  description: string
  status: string
  result_code?: string
  result_desc?: string
  mpesa_receipt?: string
  checkout_request_id: string
  created_at?: string
}

export type BalanceQuery = {
  id: string
  account_name: string
  account_number: string
  status: string
  result_code?: string
  currency?: string
  working_balance?: string
  available_balance?: string
  ledger_balance?: string
  created_at?: string
}

const page = (limit: number, offset: number, q = '', stuckOnly = false, attentionOnly = false) =>
  `limit=${limit}&offset=${offset}${q ? `&q=${encodeURIComponent(q)}` : ''}${stuckOnly ? '&stuck=true' : ''}${attentionOnly ? '&attention=true' : ''}`

// A transaction detail is the full row as flat scalars plus the raw gateway payload (arbitrary JSON).
export type TransactionDetail = Record<string, unknown> & { id: string; payload?: unknown }

// ---- reads ------------------------------------------------------------------
export const getSummary = () => api.get<Summary>('/payments/summary')
export const listAccounts = () => api.get<Account[]>('/payments/accounts')
export const listTransactionsIn = (limit = 50, offset = 0, q = '') =>
  api.getPaged<TransactionIn>(`/payments/transactions-in?${page(limit, offset, q)}`)
export const listTransactionsOut = (limit = 50, offset = 0, q = '', stuckOnly = false, attentionOnly = false) =>
  api.getPaged<TransactionOut>(`/payments/transactions-out?${page(limit, offset, q, stuckOnly, attentionOnly)}`)
export const listStkRequests = (limit = 50, offset = 0, q = '') =>
  api.getPaged<StkRequest>(`/payments/mpesa/stk-requests?${page(limit, offset, q)}`)
export const listBalanceQueries = (limit = 50, offset = 0, q = '') =>
  api.getPaged<BalanceQuery>(`/payments/mpesa/balance-queries?${page(limit, offset, q)}`)

// ---- detail (row + raw payload) ---------------------------------------------
export const getTransactionInDetail = (id: string) => api.get<TransactionDetail>(`/payments/transactions-in/${id}`)
export const getTransactionOutDetail = (id: string) => api.get<TransactionDetail>(`/payments/transactions-out/${id}`)
export const getStkDetail = (id: string) => api.get<TransactionDetail>(`/payments/mpesa/stk-requests/${id}`)
export const getBalanceDetail = (id: string) => api.get<TransactionDetail>(`/payments/mpesa/balance-queries/${id}`)

export type { Paged }

// ---- configure / diagnostic actions (manage:config) -------------------------
// Console actions key on the account's unique id (account_id) — a shortcode can belong to several accounts
// (collection vs disbursement), so the id guarantees the account the operator picked is the one that transacts.
export const registerUrls = (body: { account_id: string; base_url: string }) =>
  api.post<{ ok: boolean }>('/payments/mpesa/register-urls', body)
export const stkPush = (body: { account_id: string; phone: string; amount: string; reference?: string; description?: string; base_url?: string }) =>
  api.post<{ ok: boolean; checkout_request_id?: string }>('/payments/mpesa/stk-push', body)
export const queryBalance = (body: { account_id: string }) =>
  api.post<{ ok: boolean; conversation_id?: string }>('/payments/mpesa/balance', body)

// ---- rail test actions (fixed-amount payouts + C2B simulate) ----------------
type PayoutAck = { id?: string; status?: string; idempotent?: boolean }
export const submitB2C = (body: { account_id: string; phone: string; amount: string; reference: string; instruction_id: string; remarks?: string }) =>
  api.post<PayoutAck>('/payments/mpesa/b2c', body)
export const submitPochi = (body: { account_id: string; phone: string; amount: string; reference: string; instruction_id: string; remarks?: string }) =>
  api.post<PayoutAck>('/payments/mpesa/pochi', body)
export const submitB2B = (body: { type: 'buygoods' | 'paybill'; account_id: string; receiving_short_code: string; account_number?: string; requester_phone: string; amount: string; reference: string; instruction_id: string; remarks?: string }) =>
  api.post<PayoutAck>('/payments/mpesa/b2b', body)
/** Force the outbox dispatcher to send queued payouts now (needed when RUN_JOBS is off). */
export const dispatchPayouts = () => api.post<{ dispatched: number }>('/payments/mpesa/dispatch')
/** Re-check a stuck/abandoned payout: reset its status-query backoff and query Safaricom again now. */
export const recheckPayout = (reference: string) =>
  api.post<{ ok: boolean; status?: string; detail?: string }>('/payments/mpesa/recheck', { reference })
/** Sandbox-only: simulate an inbound C2B collection (fixed KES 1) to a paybill. */
export const simulateC2B = (body: { account_id: string; phone: string; bill_ref?: string }) =>
  api.post<{ ok: boolean }>('/payments/mpesa/simulate', body)

export type Gateway = {
  key: string
  name: string
  direction: 'in' | 'out'
  configured: boolean
  accounts: number
  volume: number
  delivered: number
  failed: number
  reversed: number
  pending: number
  last_activity?: string
}
export const listGateways = () => api.get<Gateway[]>('/payments/gateways')

// ---- account management (manage:config) -------------------------------------
export type AccountCredentials = {
  key?: string
  secret?: string
  passkey?: string
  initiator?: string
  initiator_password?: string
  security_credential?: string
}
export const createAccount = (body: { name: string; number: string; role: string } & AccountCredentials) =>
  api.post<{ ok: boolean; id: string }>('/payments/accounts', body)
export const setAccountActive = (id: string, active: boolean) =>
  api.patch<{ ok: boolean; active: boolean }>(`/payments/accounts/${id}`, { active })
export const updateAccountCredentials = (id: string, creds: AccountCredentials) =>
  api.post<{ ok: boolean }>(`/payments/accounts/${id}/credentials`, creds)

// ---- system config (manage:config) ------------------------------------------
export type ConfigItem = { label: string; value: string; tone?: string }
export type ConfigGroup = { title: string; items: ConfigItem[] }
// Current editable values. Secret fields are never returned in the clear — only a *_set flag.
export type EditableConfig = {
  public_base_url: string
  mpesa_url: string
  stuck_payout_minutes: number
  money_out_paused: boolean
  callback_pass_set: boolean
  smtp_host: string
  smtp_port: string
  smtp_username: string
  smtp_password_set: boolean
  mail_from: string
  mail_brand_color: string
  mail_tagline: string
  mail_support_email: string
  event_relay: boolean
  rabbitmq_host: string
  rabbitmq_port: string
  rabbitmq_username: string
  rabbitmq_password_set: boolean
  rabbitmq_tls: boolean
  rabbitmq_exchange: string
  event_queue: string
  event_routing_prefix: string
  event_source: string
  mpesa_cert_set: boolean
  login_max_attempts: number
  login_lock_minutes: number
  email_otp_enabled: boolean
  email_otp_ttl_minutes: number
  otp_max_attempts: number
  access_ttl: string
  refresh_ttl: string
  session_idle_timeout: string
  session_max_lifetime: string
}
export type SystemConfig = {
  editable: EditableConfig
  overrides: string[] // editable keys currently overriding env
  restart_keys: string[] // editable keys that only take effect on restart
  groups: ConfigGroup[]
  warnings: string[]
}
// Every field optional; a nil/omitted field (or a blank secret) leaves the current value untouched.
export type ConfigUpdate = {
  public_base_url?: string
  mpesa_url?: string
  mpesa_callback_password?: string
  stuck_payout_minutes?: number
  money_out_paused?: boolean
  smtp_host?: string
  smtp_port?: string
  smtp_username?: string
  smtp_password?: string
  mail_from?: string
  mail_brand_color?: string
  mail_tagline?: string
  mail_support_email?: string
  event_relay?: boolean
  rabbitmq_host?: string
  rabbitmq_port?: string
  rabbitmq_username?: string
  rabbitmq_password?: string
  rabbitmq_tls?: boolean
  rabbitmq_exchange?: string
  event_queue?: string
  event_routing_prefix?: string
  event_source?: string
  mpesa_cert?: string
  login_max_attempts?: number
  login_lock_minutes?: number
  email_otp_enabled?: boolean
  email_otp_ttl_minutes?: number
  otp_max_attempts?: number
  access_ttl?: string
  refresh_ttl?: string
  session_idle_timeout?: string
  session_max_lifetime?: string
}
export const getSystemConfig = () => api.get<SystemConfig>('/payments/config')
export const updateSystemConfig = (body: ConfigUpdate) => api.patch<SystemConfig>('/payments/config', body)
